import * as React from 'react';
import { connect } from 'react-redux';
import { provideLocalizationService, provideIntlService, registerForLocalization, registerForIntl } from '@progress/kendo-react-intl';
import { Button, SplitButton } from '@progress/kendo-react-buttons';
import { Skeleton } from '@progress/kendo-react-indicators';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { OverviewPage, StackLayout, Text } from '../../../ui';
import { ErrorMessage } from '../../../ui/components/common';
import { DateFormatter, EmptyFormatter, ValueItem } from '../../shared/components';
import { loadEventOverviewPage, changeEventStatus, openEventOverviewConfirmDialog, closeEventOverviewConfirmDialog, removeEventOverviewConfirmDialog, selectEventOverviewPageTab } from '../actions/event-overview-page-actions';
import { GeneralCompetencyEventSessionList } from './';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { withRouter } from '../../../components/withRouter';
import { GENERAL_COMPETENCY_EVENT_STATUSES } from '../../../resources/generalCompetencyEventStatus';

const STATUS_CONFIRM_MESSAGES = {
  [GENERAL_COMPETENCY_EVENT_STATUSES.PUBLISHED]: 'generalCompetencyEvent.confirmPublish',
  [GENERAL_COMPETENCY_EVENT_STATUSES.DRAFT]: 'generalCompetencyEvent.confirmUnpublish',
  [GENERAL_COMPETENCY_EVENT_STATUSES.CANCELLED]: 'generalCompetencyEvent.confirmCancel'
};

class GeneralCompetencyEventOverviewPage extends React.PureComponent {

  constructor() {
    super();
    this.state = { pendingStatus: null };
    this.handleEditClick = this.handleEditClick.bind(this);
    this.handleBreadcrumbItemClick = this.handleBreadcrumbItemClick.bind(this);
    this.handleBackClick = this.handleBackClick.bind(this);
    this.handleTabSelect = this.handleTabSelect.bind(this);
    this.handleSplitButtonItemClick = this.handleSplitButtonItemClick.bind(this);
    this.handleSplitButtonPrimaryClick = this.handleSplitButtonPrimaryClick.bind(this);
    this.handleStatusConfirm = this.handleStatusConfirm.bind(this);
    this.handleStatusConfirmClose = this.handleStatusConfirmClose.bind(this);
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.handleConfirmDialogClose = this.handleConfirmDialogClose.bind(this);
    this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
  }

  async componentDidMount() {
    if (this.props.params?.generalCompetencyEventId) {
      this.props.loadEventOverviewPage({
        eventId: this.props.params.generalCompetencyEventId
      });
    }
  }

  render() {
    const { event, loading, canWrite } = this.props;
    const localizationService = provideLocalizationService(this);
    const intlService = provideIntlService(this);
    const isDraft = event?.status === GENERAL_COMPETENCY_EVENT_STATUSES.DRAFT;
    const isCompleted = event?.status === GENERAL_COMPETENCY_EVENT_STATUSES.COMPLETED;
    const canEdit = canWrite && isDraft;
    const canDelete = canEdit && !event?.hasSessions;
    return (
      <>
        <OverviewPage
          title={loading
            ? <Skeleton shape='text' style={{ width: 300, height: 28 }}/>
            : EmptyFormatter(event?.name)
          }
          headerActions={
            <>
              {canWrite &&
                <SplitButton
                  themeColor={'primary'}
                  text={this.getPrimaryButtonText(event?.status, localizationService)}
                  disabled={isCompleted || event?.status === GENERAL_COMPETENCY_EVENT_STATUSES.CANCELLED}
                  items={this.renderSplitButtonItems(event?.status, localizationService)}
                  onButtonClick={isDraft ? this.handleEditClick : this.handleSplitButtonPrimaryClick}
                  onItemClick={this.handleSplitButtonItemClick}
                />
              }
              {canWrite &&
                <Button
                  type='button'
                  disabled={!canDelete}
                  onClick={this.handleDeleteClick}
                >
                  {localizationService.toLanguageString('buttons.delete')}
                </Button>
              }
            </>
          }
          breadcrumbItems={[
            {
              id: 'general_competency_events',
              text: localizationService.toLanguageString('generalCompetencyEvent.listTitle')
            },
            {
              id: 'general_competency_event',
              text: localizationService.toLanguageString('generalCompetencyEvent.overviewTitle'),
              disabled: true
            }
          ]}
          onBreadcrumbItemClick={this.handleBreadcrumbItemClick}
          onBackClick={this.handleBackClick}
          summary={
            <StackLayout rowGap='8px'>
              <ValueItem
                label={localizationService.toLanguageString('generalCompetencyEvent.description')}
                labelWidth='170px'
                loading={loading}
              >
                <Text variant={'body2'} overflowWrap={true}>
                  {EmptyFormatter(event?.description)}
                </Text>
              </ValueItem>
              <ValueItem
                label={localizationService.toLanguageString('generalCompetencyEvent.status')}
                labelWidth='170px'
                loading={loading}
              >
                {event?.status ? this.getLocalizedStatus(event.status, localizationService) : EmptyFormatter(null)}
              </ValueItem>
              <ValueItem
                label={localizationService.toLanguageString('generalCompetencyEvent.startDate')}
                labelWidth='170px'
                loading={loading}
              >
                {EmptyFormatter(DateFormatter(event?.startDate, intlService))}
              </ValueItem>
              <ValueItem
                label={localizationService.toLanguageString('generalCompetencyEvent.endDate')}
                labelWidth='170px'
                loading={loading}
              >
                {EmptyFormatter(DateFormatter(event?.endDate, intlService))}
              </ValueItem>
            </StackLayout>
          }
          tabs={
            <TabStrip selected={this.props.selectedTab} onSelect={this.handleTabSelect}>
              <TabStripTab title={localizationService.toLanguageString('generalCompetencyEvent.sessions')}/>
            </TabStrip>
          }
        >
          {this.props.selectedTab === 0 &&
            <GeneralCompetencyEventSessionList
              eventId={this.props.params.generalCompetencyEventId}
              readOnly={!canEdit}
            />
          }
        </OverviewPage>
        {this.state.pendingStatus &&
          <Dialog title={localizationService.toLanguageString('custom.confirm')} onClose={this.handleStatusConfirmClose}>
            <Text>{localizationService.toLanguageString(STATUS_CONFIRM_MESSAGES[this.state.pendingStatus])}</Text>
            <DialogActionsBar>
              <Button themeColor={'primary'} onClick={this.handleStatusConfirm}>{localizationService.toLanguageString('buttons.confirm')}</Button>
              <Button onClick={this.handleStatusConfirmClose}>{localizationService.toLanguageString('buttons.cancel')}</Button>
            </DialogActionsBar>
          </Dialog>
        }
        {this.props.isConfirmDialogVisible &&
          <Dialog title={localizationService.toLanguageString('custom.confirm')} onClose={this.handleConfirmDialogClose}>
            {this.props.confirmDialogErrorMessage &&
              <ErrorMessage>{this.props.confirmDialogErrorMessage}</ErrorMessage>
            }
            <Text>{localizationService.toLanguageString('generalCompetencyEvent.deleteConfirmDialogMessage')}</Text>
            <DialogActionsBar>
              <Button themeColor={'primary'} onClick={this.handleDeleteConfirm}>{localizationService.toLanguageString('buttons.delete')}</Button>
              <Button onClick={this.handleConfirmDialogClose}>{localizationService.toLanguageString('buttons.cancel')}</Button>
            </DialogActionsBar>
          </Dialog>
        }
      </>
    );
  }

  getPrimaryButtonText(status, localizationService) {
    switch (status) {
      case GENERAL_COMPETENCY_EVENT_STATUSES.DRAFT:
        return localizationService.toLanguageString('custom.edit');
      case GENERAL_COMPETENCY_EVENT_STATUSES.PUBLISHED:
        return localizationService.toLanguageString('generalCompetencyEvent.actionUnpublish');
      default:
        return localizationService.toLanguageString('custom.edit');
    }
  }

  renderSplitButtonItems(status, localizationService) {
    const isDraft = status === GENERAL_COMPETENCY_EVENT_STATUSES.DRAFT;
    const isPublished = status === GENERAL_COMPETENCY_EVENT_STATUSES.PUBLISHED;
    return [
      { id: 'edit', text: localizationService.toLanguageString('custom.edit'), disabled: !isDraft, hidden: isDraft },
      { id: GENERAL_COMPETENCY_EVENT_STATUSES.PUBLISHED, text: localizationService.toLanguageString('generalCompetencyEvent.actionPublish'), disabled: !isDraft },
      { id: GENERAL_COMPETENCY_EVENT_STATUSES.DRAFT, text: localizationService.toLanguageString('generalCompetencyEvent.actionUnpublish'), disabled: !isPublished, hidden: isPublished },
      { id: GENERAL_COMPETENCY_EVENT_STATUSES.CANCELLED, text: localizationService.toLanguageString('generalCompetencyEvent.actionCancel'), disabled: !isDraft && !isPublished }
    ].filter(item => !item.hidden);
  }

  handleSplitButtonPrimaryClick() {
    this.setState({ pendingStatus: GENERAL_COMPETENCY_EVENT_STATUSES.DRAFT });
  }

  handleSplitButtonItemClick(e) {
    if (e.item.disabled) {
      return;
    }
    if (e.item.id === 'edit') {
      this.handleEditClick();
    } else {
      this.setState({ pendingStatus: e.item.id });
    }
  }

  handleStatusConfirm() {
    this.props.changeEventStatus({
      eventId: this.props.params.generalCompetencyEventId,
      status: this.state.pendingStatus
    });
    this.setState({ pendingStatus: null });
  }

  handleStatusConfirmClose() {
    this.setState({ pendingStatus: null });
  }

  getLocalizedStatus(status, localizationService) {
    const statusMap = {
      'Draft': 'generalCompetencyEvent.statusDraft',
      'Published': 'generalCompetencyEvent.statusPublished',
      'Completed': 'generalCompetencyEvent.statusCompleted',
      'Cancelled': 'generalCompetencyEvent.statusCancelled'
    };
    const key = statusMap[status];
    return key ? localizationService.toLanguageString(key) : status;
  }

  handleEditClick() {
    this.props.navigate(`/general_competency_events/${this.props.params.generalCompetencyEventId}`);
  }

  handleBreadcrumbItemClick(e) {
    if (e.id === 'general_competency_events') {
      this.props.navigate('/general_competency_events');
    }
  }

  handleBackClick() {
    this.props.navigate(-1);
  }

  handleTabSelect(e) {
    this.props.selectEventOverviewPageTab({
      selected: e.selected
    });
  }

  handleDeleteClick() {
    this.props.openEventOverviewConfirmDialog();
  }

  handleConfirmDialogClose() {
    this.props.closeEventOverviewConfirmDialog();
  }

  handleDeleteConfirm() {
    this.props.removeEventOverviewConfirmDialog({
      eventId: this.props.params.generalCompetencyEventId,
      navigate: this.props.navigate
    });
  }
}

registerForLocalization(GeneralCompetencyEventOverviewPage);
registerForIntl(GeneralCompetencyEventOverviewPage);

const mapStateToProps = state => ({
  event: state.generalCompetencyEventOverviewPage.event,
  loading: state.generalCompetencyEventOverviewPage.loading,
  selectedTab: state.generalCompetencyEventOverviewPage.selectedTab,
  canWrite: state.generalCompetencyEventOverviewPage.canWrite,
  isConfirmDialogVisible: state.generalCompetencyEventOverviewPage.isConfirmDialogVisible,
  confirmDialogErrorMessage: state.generalCompetencyEventOverviewPage.confirmDialogErrorMessage
});

const mapDispatchToProps = dispatch => ({
  loadEventOverviewPage: (payload) => dispatch(loadEventOverviewPage(payload)),
  changeEventStatus: (payload) => dispatch(changeEventStatus(payload)),
  openEventOverviewConfirmDialog: () => dispatch(openEventOverviewConfirmDialog()),
  closeEventOverviewConfirmDialog: () => dispatch(closeEventOverviewConfirmDialog()),
  removeEventOverviewConfirmDialog: (payload) => dispatch(removeEventOverviewConfirmDialog(payload)),
  selectEventOverviewPageTab: (payload) => dispatch(selectEventOverviewPageTab(payload))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(GeneralCompetencyEventOverviewPage));
